import { unitPacketDamage } from "./combat-packets.js";
import {
  type CombatReport,
  type CombatResult,
  type CombatSeatInput,
  type CombatSeatResult,
  INFRASTRUCTURE_FALLBACK,
  type InfrastructureTarget,
} from "./combat-types.js";
import { buildingRule, unitRule } from "./economy-rules.js";
import {
  addRational,
  divideRational,
  formatMicro,
  microFromString,
  multiplyRational,
  type Rational,
  rational,
  rationalFromDecimal,
  settleMicroFloor,
} from "./exact-numbers.js";
import { type TroopComposition, UNIT_IDS } from "./logistics-types.js";
import { effectMultiplier } from "./research-effects.js";

const ZERO = rational(0n);
const ONE = rational(1n);
const MICRO_SCALE = 1_000_000n;

interface Packet {
  readonly attackerId: string;
  readonly defenderId: string;
  readonly troopPayload: Rational;
  readonly infrastructurePayload: Rational;
  readonly troopShield: Rational;
  readonly infrastructureShield: Rational;
  readonly effectiveTarget: InfrastructureTarget;
}

interface ResolvedPacket extends Packet {
  readonly absorbed: bigint;
  readonly troopDamage: Rational;
  readonly infrastructureDamage: Rational;
  readonly blastDoorsActive: boolean;
}

export function settleCombat(seats: readonly CombatSeatInput[], elapsedMs: number): CombatResult {
  if (!Number.isSafeInteger(elapsedMs) || elapsedMs < 0)
    throw new TypeError("Combat elapsedMs must be a nonnegative safe integer");
  const ordered = [...seats].sort((left, right) => left.id.localeCompare(right.id));
  const byId = new Map(ordered.map((seat) => [seat.id, seat]));
  if (byId.size !== ordered.length) throw new TypeError("Combat seat IDs must be unique");
  const packets: Packet[] = [];
  for (const attacker of ordered) {
    for (const defenderId of Object.keys(attacker.borders).sort()) {
      const defender = byId.get(defenderId);
      const attackBorder = attacker.borders[defenderId];
      const defenseBorder = defender?.borders[attacker.id];
      if (!defender || !attackBorder || !defenseBorder)
        throw new TypeError(`Combat border must be symmetric: ${attacker.id} <-> ${defenderId}`);
      if (!alive(attacker) || !alive(defender)) continue;
      if (attackBorder.standingOrder === "hold" && defenseBorder.standingOrder === "hold") continue;
      const packet = constructPacket(attacker, defender, elapsedMs);
      if (packet) packets.push(packet);
    }
  }

  const resolved = packets.map((packet) => resolveShield(packet, byId));
  const breached = resolved
    .filter(({ defenderId, attackerId, absorbed }) => {
      const charge = microFromString(
        required(byId, defenderId).borders[attackerId]?.shieldCharge ?? "0",
      );
      return charge > 0n && absorbed >= charge;
    })
    .map(({ defenderId, attackerId }) => ({ seatId: defenderId, borderId: attackerId }));
  return Object.freeze({
    seats: Object.freeze(ordered.map((seat) => applySeatResult(seat, resolved, byId))),
    breached: Object.freeze(breached),
    reports: Object.freeze(
      resolved.map((packet) => ({
        attackerId: packet.attackerId,
        targetId: packet.defenderId,
        ...reportFor(packet),
      })),
    ),
  });
}

function constructPacket(
  attacker: CombatSeatInput,
  defender: CombatSeatInput,
  elapsedMs: number,
): Packet | null {
  const border = attacker.borders[defender.id];
  const defense = defender.borders[attacker.id];
  if (!border || !defense) return null;
  const hasDefenders = UNIT_IDS.some((id) => microFromString(defense.troops[id]) > 0n);
  if (border.standingOrder === "hold" && !hasDefenders) return null;
  const target = effectiveTarget(border.requestedTarget, defender);
  let troopPayload = ZERO;
  let infrastructurePayload = ZERO;
  let troopShield = ZERO;
  let infrastructureShield = ZERO;
  const supply = rationalFromDecimal(border.supplyEffectiveness);
  const duration = rational(BigInt(elapsedMs), 1_000n);
  const shieldWasEmpty = microFromString(defense.shieldCharge) === 0n;

  for (const id of UNIT_IDS) {
    const damage = unitPacketDamage(
      id,
      border.troops[id],
      border.standingOrder,
      hasDefenders,
      shieldWasEmpty,
      supply,
      duration,
      attacker,
    );
    troopPayload = addRational(troopPayload, damage.troopPayload);
    infrastructurePayload = addRational(infrastructurePayload, damage.infrastructurePayload);
    troopShield = addRational(troopShield, damage.troopShield);
    infrastructureShield = addRational(infrastructureShield, damage.infrastructureShield);
  }
  if (troopPayload.numerator === 0n && infrastructurePayload.numerator === 0n) return null;
  return {
    attackerId: attacker.id,
    defenderId: defender.id,
    troopPayload,
    infrastructurePayload,
    troopShield,
    infrastructureShield,
    effectiveTarget: target,
  };
}

function resolveShield(
  packet: Packet,
  seats: ReadonlyMap<string, CombatSeatInput>,
): ResolvedPacket {
  const defender = required(seats, packet.defenderId);
  const border = defender.borders[packet.attackerId];
  if (!border) throw new TypeError("Missing defending border");
  const shieldDamage = addRational(packet.troopShield, packet.infrastructureShield);
  const charge = microFromString(border.shieldCharge);
  const absorbed = min(charge, settleMicroFloor(shieldDamage));
  const through =
    shieldDamage.numerator === 0n
      ? ONE
      : divideRational(subtract(shieldDamage, fromMicro(absorbed)), shieldDamage);
  const blastDoorsActive = hasBlastDoors(defender.operatedShieldGenerators);
  const doorFactor =
    packet.effectiveTarget === "headquarters" && blastDoorsActive ? rational(1n, 4n) : ONE;
  return {
    ...packet,
    absorbed,
    troopDamage: multiplyRational(packet.troopPayload, through),
    infrastructureDamage: multiplyRational(
      multiplyRational(packet.infrastructurePayload, through),
      doorFactor,
    ),
    blastDoorsActive,
  };
}

export function hasBlastDoors(operatedShieldGenerators: string): boolean {
  return microFromString(operatedShieldGenerators) >= MICRO_SCALE;
}

function applySeatResult(
  seat: CombatSeatInput,
  packets: readonly ResolvedPacket[],
  seats: ReadonlyMap<string, CombatSeatInput>,
): CombatSeatResult {
  const incoming = packets.filter((packet) => packet.defenderId === seat.id);
  const outgoing = new Map(
    packets
      .filter((packet) => packet.attackerId === seat.id)
      .map((packet) => [packet.defenderId, packet]),
  );
  const borders = Object.freeze(
    Object.fromEntries(
      Object.entries(seat.borders).map(([borderId, border]) => {
        const packet = incoming.find((candidate) => candidate.attackerId === borderId);
        const attack = outgoing.get(borderId);
        return [
          borderId,
          Object.freeze({
            troops: packet
              ? applyCasualties(border.troops, packet.troopDamage, seat)
              : border.troops,
            shieldCharge: packet
              ? formatMicro((microFromString(border.shieldCharge) - packet.absorbed) as never)
              : border.shieldCharge,
            effectiveTarget: effectiveTarget(border.requestedTarget, required(seats, borderId)),
            lastAttack: attack ? reportFor(attack) : null,
          }),
        ];
      }),
    ),
  );
  const buildings = { ...seat.buildings };
  const wreckage = { ...seat.wreckage };
  let headquarters: bigint = microFromString(seat.headquarters);
  for (const target of INFRASTRUCTURE_FALLBACK) {
    const damage = incoming
      .filter((packet) => packet.effectiveTarget === target)
      .reduce((sum, packet) => addRational(sum, packet.infrastructureDamage), ZERO);
    if (damage.numerator === 0n) continue;
    if (target === "headquarters") {
      headquarters = subtractMicro(headquarters, settleMicroFloor(damage));
      continue;
    }
    const armor = rationalFromDecimal(
      effectMultiplier(seat.researchEffects, "non-hq-building-integrity"),
    );
    const integrity = multiplyRational(rational(BigInt(buildingRule(target).metalCost * 2)), armor);
    const destroyed = min(
      microFromString(buildings[target]),
      settleMicroFloor(divideRational(damage, integrity)),
    );
    buildings[target] = formatMicro((microFromString(buildings[target]) - destroyed) as never);
    wreckage[target] = formatMicro((microFromString(wreckage[target]) + destroyed) as never);
  }
  return Object.freeze({
    id: seat.id,
    headquarters: formatMicro(headquarters as never),
    buildings: Object.freeze(buildings),
    wreckage: Object.freeze(wreckage),
    borders,
  });
}

function applyCasualties(
  troops: TroopComposition,
  damage: Rational,
  defender: CombatSeatInput,
): TroopComposition {
  const integrity = Object.fromEntries(
    UNIT_IDS.map((id) => {
      const multiplier = effectMultiplier(
        defender.researchEffects,
        id === "guard-robot"
          ? "guard-integrity"
          : id === "raider-robot"
            ? "raider-integrity"
            : "siege-integrity",
      );
      return [
        id,
        multiplyRational(rational(BigInt(unitRule(id).integrity)), rationalFromDecimal(multiplier)),
      ];
    }),
  ) as Record<(typeof UNIT_IDS)[number], Rational>;
  const total = UNIT_IDS.reduce(
    (sum, id) => addRational(sum, multiplyRational(rationalFromDecimal(troops[id]), integrity[id])),
    ZERO,
  );
  if (total.numerator === 0n || damage.numerator === 0n) return troops;
  return Object.freeze(
    Object.fromEntries(
      UNIT_IDS.map((id) => {
        const strength = microFromString(troops[id]);
        const typeIntegrity = multiplyRational(rationalFromDecimal(troops[id]), integrity[id]);
        const share = multiplyRational(damage, divideRational(typeIntegrity, total));
        const destroyed = min(strength, settleMicroFloor(divideRational(share, integrity[id])));
        return [id, formatMicro((strength - destroyed) as never)];
      }),
    ),
  ) as TroopComposition;
}

function effectiveTarget(
  requested: InfrastructureTarget,
  defender: CombatSeatInput,
): InfrastructureTarget {
  if (requested === "headquarters" || microFromString(defender.buildings[requested]) > 0n)
    return requested;
  return (
    INFRASTRUCTURE_FALLBACK.find(
      (target) => target === "headquarters" || microFromString(defender.buildings[target]) > 0n,
    ) ?? "headquarters"
  );
}

function reportFor(packet: ResolvedPacket): CombatReport {
  return Object.freeze({
    effectiveTarget: packet.effectiveTarget,
    shieldAbsorbed: formatMicro(packet.absorbed as never),
    troopDamage: formatMicro(settleMicroFloor(packet.troopDamage)),
    infrastructureDamage: formatMicro(settleMicroFloor(packet.infrastructureDamage)),
    blastDoorsActive: packet.blastDoorsActive,
  });
}

function alive(seat: CombatSeatInput): boolean {
  return microFromString(seat.headquarters) > 0n;
}

function required(seats: ReadonlyMap<string, CombatSeatInput>, id: string): CombatSeatInput {
  const seat = seats.get(id);
  if (!seat) throw new TypeError(`Unknown combat seat: ${id}`);
  return seat;
}

function fromMicro(value: bigint): Rational {
  return rational(value, MICRO_SCALE);
}

function subtract(left: Rational, right: Rational): Rational {
  return addRational(left, rational(-right.numerator, right.denominator));
}

function min(left: bigint, right: bigint): bigint {
  return left < right ? left : right;
}

function subtractMicro(left: bigint, right: bigint): bigint {
  return left > right ? left - right : 0n;
}
